import { useState, type FormEvent, type ReactNode } from 'react';

import { Button, Input } from '@/components/ui';

export type StubFormValues = {
  email: string;
  fullName: string;
  cardNumber: string;
};

type StubPaymentFormProps = {
  defaultEmail?: string;
  defaultName?: string;
  /** When true, hide the user-info inputs (server reads from auth.users — Q9). */
  collectUserInfo?: boolean;
  disabled?: boolean;
  pending?: boolean;
  onSubmit: (values: StubFormValues) => void;
  /**
   * Slot renderizado ENTRE los campos de tarjeta y el botón "Pagar", dentro del
   * mismo <form>. Lo usa el checkout para meter el selector de cuotas de modo que
   * el botón de pago quede como último elemento del flujo (input → cuotas → pagar).
   */
  children?: ReactNode;
};

/**
 * StubPaymentForm — the visual surface that maps to `StubAdapter.initiate()`.
 *
 * Per Q9 resolution: email + name come from `auth.users` via the JWT
 * server-side. The form does NOT need to collect them. But for smoke
 * E2E tests + tester convenience, we render them as read-only fields
 * populated from the session.
 *
 * Card number is fake — the user types anything 13-19 chars long; the
 * StubAdapter ignores it. The last char of the persisted Idempotency-Key
 * controls the outcome (see ADR-0003).
 *
 * Submit calls `onSubmit({email, fullName, cardNumber})`. The parent
 * (Checkout.tsx) translates that to:
 *   1. POST /payment/initiate → receive paymentId + flow:'stub_immediate' + stubWebhookPayload (via local adapter)
 *   2. POST /payment/webhook/stub with that payload
 *   3. Polling /order/:id until status=confirmed → navigate /checkout/success
 */
export function StubPaymentForm({
  defaultEmail = '',
  defaultName = '',
  collectUserInfo = false,
  disabled = false,
  pending = false,
  onSubmit,
  children,
}: StubPaymentFormProps): JSX.Element {
  const [email, setEmail] = useState(defaultEmail);
  const [fullName, setFullName] = useState(defaultName);
  const [cardNumber, setCardNumber] = useState('4242 4242 4242 4242');

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (disabled || pending) return;
    onSubmit({ email, fullName, cardNumber: cardNumber.replace(/\s+/g, '') });
  }

  return (
    <form onSubmit={handleSubmit} className="mt-2 flex flex-col gap-4">
      {collectUserInfo && (
        <>
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={disabled}
          />
          <Input
            label="Nombre completo"
            autoComplete="name"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            disabled={disabled}
          />
        </>
      )}
      <Input
        label="Número de tarjeta (sandbox)"
        autoComplete="cc-number"
        inputMode="numeric"
        pattern="[0-9 ]*"
        value={cardNumber}
        onChange={(e) => setCardNumber(e.target.value)}
        hint="Cualquier número funciona en este sandbox. El último carácter del Idempotency-Key controla el resultado (9→falla, 0→3DS, resto→éxito)."
        disabled={disabled}
      />
      {children}
      <Button type="submit" disabled={disabled || pending} variant="primary" size="lg">
        {pending ? 'Procesando pago…' : 'Pagar'}
      </Button>
    </form>
  );
}
